import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DriverTemplate } from '../driver-template';
import { Platform } from '@ionic/angular';
import { WebcamService } from '../webcam.service';

export interface FaceRoiStatus {
  requestId?: number;
  hasFace: boolean;
  roi: { x: number; y: number; w: number; h: number };
  frame: { w: number; h: number };
}

export type DriftReason = 'moved' | 'lost' | 'persistent-drift';
export type CalibrationAction =
  | { type: 'block'; reason?: DriftReason }
  | { type: 'start-recalibration'; reason?: DriftReason }
  | { type: 'done'; reason?: string };

@Injectable({
  providedIn: 'root'
})
export class GazeWebcamService extends DriverTemplate {
  private static readonly VERBOSE = false;
  private v(...args: any[]) { if (GazeWebcamService.VERBOSE) console.debug(...args); }

  private videoElement: HTMLVideoElement | null = null;
  private running = false;
  private frameCallbackId: number | null = null;
  private lastFrameTs = 0;
  private frameWatchdogTimer: any = null;
  private readonly FRAME_STALL_MS = 2000;
  private readonly WATCHDOG_INTERVAL_MS = 1000;

  private worker?: Worker;
  private workerReady = false;
  private workerModels: { calib: boolean; gaze: boolean } = { calib: false, gaze: false };
  private faceModelLoaded = false;
  private pendingEstimate = false;
  private pendingEstimateStart = 0;
  private pendingCalibration: Map<number, { resolve: () => void; reject: (e: any) => void }> = new Map();

  private calibrationDone: boolean[] = [false, false, false, false];

  public gazePoint$ = new Subject<{ x: number; y: number; raw?: unknown }>();
  public status$ = new BehaviorSubject<string>('idle');
  public calibrationAction$ = new Subject<CalibrationAction>();
  public onCalibrationDrift?: (reason: DriftReason) => void;

  private calibModelUrl = '/assets/model/calibration.onnx';
  private gazeModelUrl = '/assets/model/gaze.onnx';

  private processingFeedAttached = false;
  private roiRequestSeq = 0;
  private pendingRoiRequests = new Map<number, { resolve: (value: FaceRoiStatus | null) => void; reject: (reason: any) => void; timeoutId: any }>();

  private perfMode: 'normal' | 'degraded' = 'normal';
  private estimateStride = 1;
  private lastBenchmarkResult: { avgMs: number; fpsAvg: number; webGPU: boolean } | null = null;

  constructor(private platform: Platform, private webcam: WebcamService) { 
    super(); 
  }

  getVideoElement() { 
    return this.videoElement || (this.videoElement = this.createProcessingVideo()); 
  }

  async start(options?: any): Promise<boolean> {
    if (this.running) return true;
    this.status$.next('loading-models');
    this.pendingEstimate = false;

    try {
      await this.webcam.start({ preferFrontCamera: true, withAudio: false, frameRate: 30 });
      const v = this.getVideoElement();
      this.webcam.attachTo(v, { mirrored: true, autoplay: true });
      await this.waitForVideoReady();
      
      if (typeof Worker === 'undefined') {
        throw new Error('Web Workers not supported');
      }
      
      await this.initWorker();
      this.running = true;
      this.myStatus.running = true;
      this.myStatus.enabled = true;
      this.status$.next('running');
      this.ensureFrameLoop();
      return true;
    } catch (e) {
      console.error('[gaze] failed to start:', e);
      this.status$.next('error');
      return false;
    }
  }

  async stop(): Promise<boolean> {
    this.running = false;
    this.pendingEstimate = false;
    this.status$.next('stopped');
    this.stopFrameWatchdog();
    this.stopCalibrationMonitoring();
    this.destroyOverlayCanvas();
    
    if (this.worker) { 
      this.worker.terminate(); 
      this.worker = undefined; 
    }

    this.workerReady = false;

    for (const [idx, pending] of this.pendingCalibration.entries()) {
      try { pending.reject('stopped'); } catch {}
    }
    this.pendingCalibration.clear();

    for (const pending of this.pendingRoiRequests.values()) {
      clearTimeout(pending.timeoutId);
      try { pending.resolve(null); } catch { }
    }

    this.pendingRoiRequests.clear();
    this.processingFeedAttached = false;
    return true;
  }

  async pause(): Promise<boolean> {
    this.running = false;
    this.pendingEstimate = false;
    this.myStatus.running = false;
    this.status$.next('paused');
    this.stopFrameWatchdog();
    return true;
  }

  async resume(): Promise<boolean> {
    if (this.running) return true;
    this.running = true;
    this.pendingEstimate = false;
    this.myStatus.running = true;
    this.status$.next('running');
    await this.ensureProcessingFeed();
    this.ensureFrameLoop();
    return true;
  }

  public async setPerformanceMode(mode: 'normal' | 'degraded'): Promise<void> {
    this.perfMode = mode;
    this.estimateStride = (mode === 'degraded') ? 2 : 1;
  }

  async restartGazeConfiguration(): Promise<void> {
    this.resetCalibration();
    if (this.worker) {
      this.worker.postMessage({ type: 'resetCalibration' });
    }
    return Promise.resolve();
  }

  resetCalibration() {
    this.calibrationDone = [false, false, false, false];
  }

  async updateCalibration(index: number, x: number, y: number): Promise<void> {
    if (index < 0 || index > 3) throw new Error('Index 0..3');
    
    await this.waitForVideoReady();
    await this.waitForWorkerModelsReady({ calib: true, face: true });
    
    const video = this.getVideoElement();
    const faceImage = await createImageBitmap(video);
    
    return new Promise<void>((resolve, reject) => {
      this.pendingCalibration.set(index, { resolve, reject });
      this.worker!.postMessage({ type: 'calibrate', index, x, y, faceImage }, [faceImage]);
    });
  }

  public async runHardwareBenchmark(iterations = 10): Promise<{ avgMs: number; fpsAvg: number; webGPU: boolean }> {
    if (this.lastBenchmarkResult && this.lastBenchmarkResult.fpsAvg >= 10) {
      this.v("[Gaze] Benchmark previamente aprovado, retornando resultado em cache");
      return this.lastBenchmarkResult;
    }
    await this.ensureProcessingFeed();
    await this.initWorker();
    await this.waitForWorkerModelsReady({ face: true });

    const video = this.getVideoElement();
    const hasWebGPU = !!(navigator as any).gpu;
    
    this.v('[Gaze] Iniciando Warmup da GPU...');
    for (let w = 0; w < 3; w++) {
      const frame = await createImageBitmap(video);
      await new Promise<void>((resolve) => {
        const reqId = 7000 + w;
        this.pendingRoiRequests.set(reqId, { 
          resolve: (_result: FaceRoiStatus | null) => resolve(), 
          reject: (_reason: any) => resolve(), 
          timeoutId: null 
        });
        this.worker?.postMessage({ type: 'roiCheck', requestId: reqId, frame }, [frame]);
      });
    }

    this.v('[Gaze] Warmup concluído. Iniciando avaliação real...');
    const times: number[] = [];

    for (let i = 0; i < iterations; i++) {
      const frame = await createImageBitmap(video);
      const t0 = performance.now();
      
      await new Promise<void>((resolve) => {
        const requestId = 8000 + i;
        const timeoutId = setTimeout(() => { 
          this.pendingRoiRequests.delete(requestId); 
          resolve(); 
        }, 2000);

        this.pendingRoiRequests.set(requestId, { 
          resolve: (_result: FaceRoiStatus | null) => { clearTimeout(timeoutId); resolve(); }, 
          reject: (_reason: any) => { clearTimeout(timeoutId); resolve(); },
          timeoutId 
        });

        this.worker?.postMessage({ type: 'roiCheck', requestId, frame }, [frame]);
      });

      times.push(performance.now() - t0);
    }

    const avgMs = times.reduce((a, b) => a + b, 0) / Math.max(1, times.length);
    const fpsAvg = 1000 / Math.max(1, avgMs);
    const result = { avgMs, fpsAvg, webGPU: hasWebGPU };
    if (fpsAvg >= 10) { this.lastBenchmarkResult = result; }
    
    return result;
  }

  public startCalibrationMonitoring(intervalSeconds = 10) {
    if (this.frameWatchdogTimer) clearInterval(this.frameWatchdogTimer);
    
    this.frameWatchdogTimer = setInterval(async () => {
      if (!this.running) return;
      const current = await this.requestRoiStatus();
      if ((!current || !current.hasFace) && this.onCalibrationDrift) {
        this.onCalibrationDrift('lost');
      }
    }, intervalSeconds * 1000);
  }

  public stopCalibrationMonitoring() {
    if (this.frameWatchdogTimer) {
      clearInterval(this.frameWatchdogTimer);
      this.frameWatchdogTimer = null;
    }
  }

  private async ensureProcessingFeed(): Promise<void> {
    if (!this.webcam.getStream()) {
      await this.webcam.start({ preferFrontCamera: true, withAudio: false, frameRate: 30 });
    }
    
    const video = this.getVideoElement();
    if (video && !this.processingFeedAttached) {
      this.webcam.attachTo(video, { mirrored: true, autoplay: true });
      this.processingFeedAttached = true;
    }
    await this.waitForVideoReady();
  }

  public async requestRoiStatus(): Promise<FaceRoiStatus | null> {
    try {
      await this.ensureProcessingFeed();
      const video = this.getVideoElement();
      const frame = await createImageBitmap(video);
      const requestId = ++this.roiRequestSeq;
      
      return await new Promise<FaceRoiStatus | null>((resolve, reject) => {
        const timeoutId = setTimeout(() => { 
          this.pendingRoiRequests.delete(requestId); 
          resolve(null); 
        }, 2000);
        
        this.pendingRoiRequests.set(requestId, { resolve, reject, timeoutId });
        this.worker?.postMessage({ type: 'roiCheck', requestId, frame }, [frame]);
      });
    } catch { 
      return null; 
    }
  }

  private async initWorker(): Promise<void> {
    if (this.worker) return;
    
    return new Promise((resolve) => {
      this.worker = new Worker(new URL('./gaze.worker', import.meta.url), { type: 'module' });
      
      this.worker.onmessage = (ev) => {
        const msg = ev.data;
        
        if (msg.type === 'preloaded') { 
          this.workerReady = true; 
          resolve(); 
        }
        
        if (msg.type === 'calibrated') {
          const pending = this.pendingCalibration.get(msg.index);
          if (pending) {
            this.pendingCalibration.delete(msg.index);
            if (msg.success) {
              this.calibrationDone[msg.index] = true;
              pending.resolve();
            } else {
              pending.reject(msg.error || 'calibration failed');
            }
          }
        }
        
        if (msg.type === 'gaze') {
          this.pendingEstimate = false;
          if (msg.success) this.gazePoint$.next({ x: msg.x, y: msg.y, raw: msg });
        }
        
        if (msg.type === 'roiStatus') {
          const pending = this.pendingRoiRequests.get(msg.requestId);
          if (pending) {
            clearTimeout(pending.timeoutId);
            this.pendingRoiRequests.delete(msg.requestId);
            pending.resolve({ requestId: msg.requestId, hasFace: !!msg.hasFace, roi: msg.roi, frame: msg.frame });
          }
        }
      };

      this.worker.postMessage({ 
        type: 'preload', 
        calibModelUrl: this.calibModelUrl, 
        gazeModelUrl: this.gazeModelUrl, 
        face: true, 
        isAndroid: this.platform.is('android') 
      });
    });
  }

  private ensureFrameLoop() {
    if (!this.running) return;
    const video = this.getVideoElement();
    
    if (video && this.frameCallbackId === null) {
      this.frameCallbackId = video.requestVideoFrameCallback((n, m) => this.frameLoop(n, m));
    }
  }

  private async frameLoop(now: number, metadata: any) {
    this.frameCallbackId = null;
    this.lastFrameTs = Date.now();
    
    if (this.running) {
      this.ensureFrameLoop();
    }
    
    if (!this.running || !this.workerReady || this.pendingEstimate) return;

    if (this.pendingEstimateStart > 0 && (Date.now() - this.pendingEstimateStart) > 5000) {
      console.warn('[gaze] pendingEstimate stuck, forcing reset');
      this.pendingEstimate = false;
      this.pendingEstimateStart = 0;
    }
    
    try {
      const faceImage = await createImageBitmap(this.getVideoElement()!);
      this.pendingEstimate = true;
      this.pendingEstimateStart = Date.now();
      this.worker?.postMessage({ type: 'estimate', faceImage }, [faceImage]);
    } catch { 
      this.pendingEstimate = false; 
      this.pendingEstimateStart = 0;
    }
  }

  private async waitForVideoReady() {
    const v = this.getVideoElement();
    const start = performance.now();
    while (v.videoWidth === 0 && performance.now() - start < 2000) {
      await new Promise(r => setTimeout(r, 50));
    }
  }

  private async waitForWorkerModelsReady(opts: any = {}) {
    const start = performance.now();
    while (!this.workerReady && performance.now() - start < 10000) {
      await new Promise(r => setTimeout(r, 50));
    }
  }

  private startFrameWatchdog() {}
  
  private stopFrameWatchdog() { 
    if (this.frameWatchdogTimer) clearInterval(this.frameWatchdogTimer); 
  }
  
  private createProcessingVideo(): HTMLVideoElement {
    const v = document.createElement('video');
    v.autoplay = v.muted = true;
    v.setAttribute('playsinline', 'true');
    Object.assign(v.style, { position: 'fixed', opacity: '0', pointerEvents: 'none', width: '1px', height: '1px', left: '-1000px' });
    document.body.appendChild(v);
    return v;
  }

  private debugOverlayEnabled = false;
  public setDebugOverlay(e: boolean) { this.debugOverlayEnabled = e; }
  public getDebugOverlay() { return this.debugOverlayEnabled; }
  private destroyOverlayCanvas() {}
  public attachPreviewTo(el: HTMLVideoElement) { this.webcam.attachTo(el); }
  public detachPreview(el?: HTMLVideoElement) { if (el) this.webcam.detachFrom(el); }
}